<script lang="ts">
  import { onMount } from "svelte"
  import { slide } from "svelte/transition"
  import { xhr, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Modal from "$app/components/Modal.svelte"
  import SymbolPicker from "$app/Pages/Plans/OnePageView/SymbolPicker.svelte"
  import SymbolUploader from "$app/Pages/Plans/OnePageView/SymbolUploader.svelte"
  import { Check } from "lucide-svelte"
  import { onboardingStore } from "../onboarding.svelte"
  import { appStore } from "$app/store.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"

  interface Props {
    statement: StatementType
    placeholder?: string
    title?: string
    description?: boolean
    symbol_picker?: boolean
    onupdate?: (statement: StatementType) => void
  }

  let {
    statement = { description: "" } as StatementType,
    placeholder = "Name",
    title = "",
    description = true,
    symbol_picker = true,
    onupdate = () => {}
  }: Props = $props()

  let form = $state({ ...statement })
  let symbolStatus = $state("selectSymbol")
  let errors: any = $state({})

  let nameInput: HTMLInputElement
  let saved = { name: false, description: false }

  let mounted = $state(false)

  async function createStatement(): Promise<boolean> {
    try {
      onboardingStore.completed = false

      const resp = await xhr.post<{ statement: StatementType }>(
        `/plans/${appStore.plan.id}/statements.json`,
        {
          statement: {
            name: form.name,
            description: form.description,
            section_type: statement.section_type
          }
        }
      )

      statement = resp.data.statement

      onupdate(statement)
      onboardingStore.completed = true
      return true
    } catch (e: any) {
      onboardingStore.completed = false
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
      return false
    }
  }

  async function updateStatement(): Promise<boolean> {
    try {
      onboardingStore.completed = false
      const resp = await xhr.put<StatementType>(
        `/plans/${appStore.plan.id}/statements/${statement.id}.json`,
        {
          statement: {
            name: form.name,
            description: form.description
          }
        }
      )

      await wait(100)

      onupdate(resp.data)
      onboardingStore.completed = true
      return true
    } catch (e: any) {
      onboardingStore.completed = false
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
      return false
    }
  }

  const waitTime = 200
  // Update statement
  async function saveStatement(field: string): Promise<void> {
    console.log("saveStatement", field)
    errors = {}
    if (field !== "name" && !statement.id) {
      return
    }

    if (statement.id) {
      if (await updateStatement()) {
        saved[field] = true
        await wait(waitTime)
        saved[field] = false
      }
      return
    }
    if (!statement.id) {
      createStatement()
      saved.name = true
      await wait(waitTime)
      saved.name = false
      return
    }
  }

  function updateSymbol(detail) {
    statement.symbol_url = detail.symbol_url
    statement.symbol_id = detail.symbol_id
    onupdate(statement)
    openSymbolModal = false
  }

  let openSymbolModal = $state(false)

  async function setOpenSymbolPicker(): Promise<void> {
    if (statement.id) {
      openSymbolModal = true
      return
    }

    // if (!saving) {
    //   alert(`Please Enter ${title} first`)
    //   return
    // }

    await wait(500)

    if (statement.id) {
      setOpenSymbolPicker()
      return
    }
    alert(`Please Enter ${title} first`)
  }

  onMount(() => {
    mounted = true

    if (form.id !== statement?.id) {
      form = { ...statement }
    }
  })
</script>

<div>
  <h2 class="text-xl text-white font-bold">{title}</h2>
  <div class="input-cont relative" class:error={errors.name}>
    <input
      type="text"
      bind:this={nameInput}
      bind:value={form.name}
      onblur={(e) => {
        saveStatement("name")
      }}
      class="input flex-auto"
      name="statement-name"
      {placeholder}
    />

    {#if saved.name}
      <Check class="text-green-500 absolute right-1 top-1" size="2rem" />
    {/if}

    <div class="msg">{errors.name}</div>
  </div>

  {#if symbol_picker}
    <div
      transition:slide
      class="mt-6 flex w-full justify-start items-center group"
    >
      <button
        class="cursor-pointer"
        data-name="symbol-picker"
        type="button"
        onclick={setOpenSymbolPicker}
      >
        <div class="flex gap-2 items-center">
          <div
            class="rounded-full border-4 flex items-center justify-center
            img-border"
            class:border-white={statement.symbol_id}
            class:border-red-500={!statement.symbol_id}
          >
            <div
              class="bg-white rounded-full size-24 flex items-center justify-center overflow-hidden"
            >
              <img
                src={statement.symbol_url}
                class="size-16"
                alt={statement.name}
              />
            </div>
          </div>
          <div
            class="text-base font-bold text-white hover:text-blue-400 hover:underline w-full"
          >
            Assign a Symbol
          </div>
        </div>
      </button>
    </div>
  {/if}

  {#if description}
    <div
      id="desc-cont"
      class:h-0={!statement.id}
      class:h-96={statement.id}
      class:overflow-none={statement.id}
    >
      <h2 class="mt-4 text-lg text-white font-bold">
        Detailed Description
        <span class="text-sm text-gray-100 h-">(Optional)</span>
      </h2>

      <div
        transition:slide
        class="text-left mt-2 w-full inline-block bg-white h-60"
      >
        <div class="mt-1 h-48">
          <Tiptap
            content={statement.description}
            onupdate={({ html }) => (form.description = html)}
            onblur={() => saveStatement("description")}
            placeholder="Enter the Brand purpose description"
            class="h-32"
            id="statement_description"
            buttons={onlyTextButtons}
            allowEmojis={true}
          />
        </div>
      </div>
    </div>
  {/if}
</div>

<Modal
  bind:open={openSymbolModal}
  closeButton={true}
  closeOnEsc={true}
  closeOnOuterClick={true}
>
  <div class="pt-6 px-2">
    {#if symbolStatus === "selectSymbol"}
      <SymbolPicker
        {statement}
        plan={appStore.plan}
        oncancel={() => (openSymbolModal = false)}
        onupdate={(detail) => updateSymbol(detail)}
      />
    {:else if symbolStatus === "uploadSymbol"}
      <SymbolUploader
        {statement}
        oncancel={() => (openSymbolModal = false)}
        onupdate={(detail) => updateSymbol(detail)}
      />
    {/if}
  </div>
</Modal>

<style>
  [data-name="symbol-picker"] .txt {
    /* @apply text-white m-4; */
    color: var(--core);
    margin-top: 1rem;
    white-space: nowrap;
  }
  [data-name="symbol-picker"]:hover .txt {
    /* @apply text-blue-400; */
    color: oklch(62.3% 0.214 259.815);
  }
  [data-name="symbol-picker"]:hover .img-border {
    /* @apply border-blue-400; */
    border-color: oklch(62.3% 0.214 259.815);
  }

  #desc-cont {
    transition: all 0.3s ease-in-out;
    overflow: hidden;
  }
</style>
